import React from 'react';
import {
  Container,
  Divider,
  Stack,
  Typography,
  TextField,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  Box,
  Button,
  useMediaQuery
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useTheme } from '@mui/material/styles';
import _ from 'lodash';
import { Icon } from '@iconify/react';
import arrowBackFilled from '@iconify/icons-eva/arrow-back-fill';
import closeCircleOutline from '@iconify/icons-eva/close-circle-outline';
import { v4 as uuidv4 } from 'uuid';
import moment from 'moment-timezone';
import eyeFill from '@iconify/icons-eva/eye-fill';
//
import * as myLayananService from '../services/mylayanan';
import * as feedbackService from '../services/feedback';
import Page from '../components/Page';
import useWidth from '../hooks/useWidth';
import isEligible from '../utils/roleEligibleGetter';

const ServicesCheckRekomendasi = ({ user }) => {
  const theme = useTheme();
  const { id } = useParams();
  const width = useWidth(useTheme, useMediaQuery);
  const [toggleRefresh, setToggleRefresh] = React.useState(false);
  const navigate = useNavigate();
  const baseStyle = {
    padding: 1,
    cursor: 'pointer'
  };

  const styles = {
    selectedMenu: {
      color: theme.palette.primary.main,
      ...baseStyle
    },
    notSelectedMenu: {
      color: '#424242',
      ...baseStyle
    }
  };
  const [selectedMenu, setSelectedMenu] = React.useState('points'); // 'points / responses / checks'

  const [myLayanan, setMyLayanan] = React.useState({});

  const getMyLayanan = async () => {
    try {
      if (!id) return;
      const result = await myLayananService.getLayananByID(id);

      setMyLayanan(result.data);
    } catch (error) {
      toast.error(error.message);
    }
  };

  React.useEffect(() => {
    getMyLayanan();
  }, [user, toggleRefresh]);
  return (
    <Page title="Pemenuhan Rekomendasi | Peladen Pemda DIY">
      <Container>
        <Stack direction="column" spacing={2}>
          <Stack direction="row" alignItems="center" spacing={2} mb={6}>
            <Icon
              cursor="pointer"
              onClick={() => navigate(-1, { replace: true })}
              icon={arrowBackFilled}
              width={32}
              height={32}
              style={{ color: theme.palette.primary.main }}
            />
            <Typography variant="h4" gutterBottom>
              Pemenuhan Rekomendasi
            </Typography>
          </Stack>
          <Typography variant="h6">
            {myLayanan?.meta?.data_permohonan?.metadata?.rekomendasi?.name}
          </Typography>
          <Divider />

          <Stack
            direction="row"
            spacing={2}
            justifyContent="center"
            divider={<Divider orientation="vertical" flexItem />}
          >
            <Typography
              onClick={() => setSelectedMenu('points')}
              variant="h6"
              sx={selectedMenu === 'points' ? styles.selectedMenu : styles.notSelectedMenu}
            >
              Poin Rekomendasi
            </Typography>
            {isEligible('check_rekomendasi') && (
              <Typography
                onClick={() => setSelectedMenu('checks')}
                variant="h6"
                sx={selectedMenu === 'checks' ? styles.selectedMenu : styles.notSelectedMenu}
              >
                Check
              </Typography>
            )}

            <Typography
              onClick={() => setSelectedMenu('responses')}
              variant="h6"
              sx={selectedMenu === 'responses' ? styles.selectedMenu : styles.notSelectedMenu}
            >
              Responses ({myLayanan?.meta?.feedback?.metadata?.checks?.length || '0'})
            </Typography>
          </Stack>

          <Divider />

          {selectedMenu === 'points' && <PoinRekomendasi myLayanan={myLayanan} user={user} />}
          {selectedMenu === 'responses' && (
            <Responses
              myLayanan={myLayanan}
              user={user}
              width={width}
              type="answer"
              setToggleRefresh={setToggleRefresh}
              toggleRefresh={toggleRefresh}
            />
          )}
          {selectedMenu === 'checks' && (
            <Checks
              myLayanan={myLayanan}
              user={user}
              width={width}
              type="answer"
              setToggleRefresh={setToggleRefresh}
              toggleRefresh={toggleRefresh}
            />
          )}
        </Stack>
      </Container>
    </Page>
  );
};

const Checks = ({ myLayanan, user, setToggleRefresh, type, toggleRefresh, prevValues }) => {
  const [saved, setIsSaved] = React.useState(false);
  const points = myLayanan?.meta?.feedback?.metadata?.answers?.point;
  const [errors, setErrors] = React.useState({});
  const [answers, setAnswers] = React.useState([]); // meta: id_question, status, comment
  const [pemeriksa, setPemeriksa] = React.useState(user?.attributes?.name);
  const [kesimpulan, setKesimpulan] = React.useState('');
  const [checkStatus, setCheckStatus] = React.useState('');
  const saveAnswer = (item, index, value, type) => {
    try {
      const data = [...answers];
      data[index] = {
        ...data[index],
        id_question: item.id,
        [type]: value
      };

      setAnswers(data);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const resetForm = () => {
    setAnswers([]);
    setKesimpulan('');
  };

  const saveAnswers = () => {
    try {
      let counter = 0;
      const errorCollector = {};
      const questionsLength = myLayanan?.meta?.feedback?.metadata?.answers?.point.length;
      answers.forEach((item, index) => {
        if (item.status) {
          errorCollector[index] = true;
          counter += 1;
        }
        if (!item.status) errorCollector[index] = true;
      });

      setErrors(errorCollector);

      if (counter !== questionsLength)
        return toast.error(`Harap isi semua pertanyaan ${questionsLength}, ${counter}`);

      if (!pemeriksa) return toast.error('Nama pemeriksa belum diisi');
      if (!kesimpulan) return toast.error('Kesimpulan belum diisi');
      if (!checkStatus) return toast.error('Status pemenuhan rekomendasi belum diisi');

      const newID = uuidv4();
      let body = {};

      const prevAnswers = Array.isArray(myLayanan?.meta?.feedback?.metadata?.checks)
        ? myLayanan?.meta?.feedback?.metadata?.checks
        : [];
      prevAnswers.push({
        meta: answers,
        id: newID,
        attributes: {
          kesimpulan,
          checkStatus,
          creator: pemeriksa,
          createdAt: moment.tz('Asia/Jakarta').format()
        }
      });

      const newAnswers = prevAnswers;

      body = {
        data: {
          id: myLayanan?.attributes?.id_feedback,
          meta: {
            ...myLayanan?.meta?.feedback?.metadata,
            checks: newAnswers
          }
        }
      };

      toast
        .promise(feedbackService.updateMetaData(body), {
          loading: 'Menyimpan data ...',
          error: <b>Maaf, terjadi kesalahan</b>,
          success: <b>Data berhasil disimpan</b>
        })
        .then(() => {
          setIsSaved(true);
          setToggleRefresh(!toggleRefresh);
          resetForm();
          window.scrollTo(0, 0);
        });
    } catch (error) {
      toast.error(error.message);
    }
  };

  const setPreviousValue = () => {
    if (type === 'response' && !_.isEmpty(prevValues)) {
      setAnswers(prevValues.meta);
      setPemeriksa(prevValues.attributes.creator);
      setKesimpulan(prevValues.attributes.kesimpulan);
      setCheckStatus(prevValues.attributes.checkStatus);
    }
  };

  React.useEffect(() => {
    setPreviousValue();
  }, [type]);
  return (
    <Container maxWidth="md">
      <Stack direction="column" spacing={2}>
        <Typography variant="body1">
          Silakan isi formulir di bawah ini untuk melakukan pengisian pengecekan pemenuhan
          rekomendasi
        </Typography>
        <Divider />
        {!_.isEmpty(points) &&
          points.map((item, index) => (
            <Stack direction="row" spacing={1}>
              <Typography>{index + 1}. </Typography>
              <Stack direction="column" spacing={1} sx={{ flex: 1 }}>
                <Typography>{item.value} </Typography>
                <FormControl>
                  <FormLabel>
                    <Typography>Hasil Pengecekan</Typography>
                  </FormLabel>
                  <RadioGroup
                    sx={{ flexDirection: 'row' }}
                    aria-labelledby="Hasil Tes"
                    name="status"
                    value={(answers && answers[index]?.status) || null}
                    onChange={(event) => {
                      if (type !== 'response')
                        saveAnswer(item, index, event.target.value, 'status');
                    }}
                  >
                    <FormControlLabel value="accept" control={<Radio />} label="Sesuai" />
                    <FormControlLabel value="reject" control={<Radio />} label="Tidak Sesuai" />
                  </RadioGroup>
                </FormControl>
                <Stack direction="row">
                  <TextField
                    disabled={type === 'response'}
                    onChange={(e) => {
                      if (type !== 'response') saveAnswer(item, index, e.target.value, 'comment');
                    }}
                    value={answers[index]?.comment}
                    multiline
                    fullWidth
                    label="Catatan"
                    placeholder="Dapat diisi dengan catatan pengujian"
                  />
                </Stack>
              </Stack>
            </Stack>
          ))}
        <Divider />
        {!_.isEmpty(points) ? (
          <Stack direction="column" spacing={2}>
            <Box>
              <TextField
                fullWidth
                disabled={type === 'response'}
                label="Nama Pemeriksa"
                placeholder="Dapat diisi nama pribadi/Instansi"
                value={pemeriksa}
                onChange={(e) => setPemeriksa(e.target.value)}
              />
            </Box>
            <Box>
              <TextField
                multiline="true"
                fullWidth
                disabled={type === 'response'}
                label="Kesimpulan"
                placeholder="Silakan isi dengan kesimpulan atau keterangan pengujian"
                value={kesimpulan}
                onChange={(e) => setKesimpulan(e.target.value)}
              />
            </Box>
            <Box>
              <FormControl>
                <FormLabel>
                  <Typography>Status</Typography>
                </FormLabel>
                <RadioGroup
                  sx={{ flexDirection: 'column' }}
                  aria-labelledby="Hasil Tes"
                  name="status"
                  value={checkStatus || null}
                  onChange={(event) => {
                    if (type !== 'response') setCheckStatus(event.target.value);
                  }}
                >
                  <FormControlLabel value="approved" control={<Radio />} label="Terpenuhi" />
                  <FormControlLabel
                    value="semi_approved"
                    control={<Radio />}
                    label="Terpenuhi dengan catatan khusus"
                  />
                  <FormControlLabel value="rejected" control={<Radio />} label="Tidak terpenuhi" />
                </RadioGroup>
              </FormControl>
            </Box>
            {type === 'answer' && (
              <Button variant="contained" onClick={() => saveAnswers()}>
                Simpan Formulir
              </Button>
            )}
          </Stack>
        ) : (
          <Typography>Belum ada poin rekomendasi</Typography>
        )}
      </Stack>
    </Container>
  );
};

const Responses = ({ myLayanan, user, setToggleRefresh, type, toggleRefresh }) => {
  const checks = myLayanan?.meta?.feedback?.metadata?.checks?.reverse();
  const theme = useTheme();
  const [check, setCheck] = React.useState({});
  return (
    <Container maxWidth="md">
      {check.id ? (
        <Stack direction="column" spacing={2}>
          <Stack direction="row" spacing={2} justifyContent="flex-start">
            <Typography>
              {check?.attributes?.creator}{' '}
              {moment.tz(check?.attributes?.createdAt, 'Asia/Jakarta').format('DD-MM-YYYY')}
            </Typography>
            <Stack
              sx={{ cursor: 'pointer' }}
              onClick={() => setCheck({})}
              direction="row"
              spacing={1}
              justifyContent="flex-start"
            >
              <Icon
                icon={closeCircleOutline}
                width={24}
                height={24}
                style={{ color: theme.palette.primary.main }}
              />
              <Typography>Tutup</Typography>
            </Stack>
          </Stack>

          <Checks
            myLayanan={myLayanan}
            user={user}
            type="response"
            setToggleRefresh={setToggleRefresh}
            toggleRefresh={toggleRefresh}
            prevValues={check}
          />
        </Stack>
      ) : (
        <Stack direction="column" spacing={1}>
          {!_.isEmpty(checks) &&
            checks.map((item, index) => (
              <Stack direction="row" spacing={1}>
                <Typography>{index + 1}.</Typography>
                <Stack direction="column" spacing={1} sx={{ flex: 1 }}>
                  <Typography>
                    {item?.attributes?.creator} -{' '}
                    {moment.tz(item?.attributes?.createdAt, 'Asia/Jakarta').format('YYYYMMDD')}
                  </Typography>
                  <Typography variant="body2">
                    Kesimpulan: {item?.attributes?.kesimpulan}
                  </Typography>
                </Stack>
                <Icon
                  cursor="pointer"
                  onClick={() => setCheck(item)}
                  icon={eyeFill}
                  width={24}
                  height={24}
                  style={{ color: theme.palette.primary.main }}
                />
              </Stack>
            ))}
        </Stack>
      )}
    </Container>
  );
};

const PoinRekomendasi = ({ myLayanan, user }) => {
  const points = myLayanan?.meta?.feedback?.metadata?.answers?.point;
  return (
    <Container maxWidth="md">
      <Stack direction="column" spacing={1}>
        {!_.isEmpty(points) &&
          points.map((item, index) => (
            <Stack direction="row" spacing={1}>
              <Typography>{index + 1}. </Typography>
              <Typography>{item.value}</Typography>
            </Stack>
          ))}
      </Stack>
    </Container>
  );
};

export default ServicesCheckRekomendasi;
